import { CartAddEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * STEELVA — Add-to-cart notification toast.
 * Listens for cart-add events and shows a compact confirmation card.
 * Used when `cart_type` is `page` (no cart drawer).
 */
class CartNotification extends HTMLElement {
  /** @type {HTMLElement | null} */
  #toast = null;
  /** @type {HTMLElement | null} */
  #progressBar = null;

  connectedCallback() {
    this.#toast = this.querySelector('.cart-notification__toast');
    this.#progressBar = this.querySelector('.cart-notification__progress-bar');

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    this.querySelector('.cart-notification__close')?.addEventListener('click', this.#hide);
    this.#progressBar?.addEventListener('animationend', this.#hide);
    this.#toast?.addEventListener('mouseenter', this.#pause);
    this.#toast?.addEventListener('mouseleave', this.#resume);
    this.#toast?.addEventListener('focusin', this.#pause);
    this.#toast?.addEventListener('focusout', this.#resume);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    if (!(event instanceof CartAddEvent)) return;
    if (event.detail?.data?.didError) return;

    const cart = event.detail?.resource;
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

    const sourceId = event.detail?.sourceId;
    const item =
      cart.items.find((entry) => String(entry.id) === String(sourceId)) ?? cart.items[cart.items.length - 1];

    this.#render(item, cart);
    this.#show();
  };

  /**
   * @param {Record<string, any>} item
   * @param {Record<string, any>} cart
   */
  #render(item, cart) {
    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('.cart-notification__image'));
    const title = this.querySelector('.cart-notification__title');
    const variant = /** @type {HTMLElement | null} */ (this.querySelector('.cart-notification__variant'));
    const price = this.querySelector('.cart-notification__price');

    const imageUrl = item.featured_image?.url || item.image;
    if (image) {
      if (imageUrl) {
        image.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=200`;
        image.alt = item.product_title || item.title || '';
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    if (title) title.textContent = item.product_title || item.title || '';

    if (variant) {
      const variantText =
        item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      variant.textContent = variantText;
      variant.hidden = variantText === '';
    }

    if (price) {
      const currency = cart.currency || window.Shopify?.currency?.active || 'USD';
      const format = this.dataset.moneyFormat || '{{amount}}';
      price.textContent = formatMoney(item.final_price ?? item.price ?? 0, format, currency);
    }
  }

  #show() {
    if (!this.#toast) return;
    this.#toast.hidden = false;
    if (this.#progressBar) this.#progressBar.style.animationPlayState = '';

    // Force a reflow so the entrance + progress animations restart on repeat adds.
    this.#toast.classList.remove('is-visible');
    void this.#toast.offsetWidth;
    this.#toast.classList.add('is-visible');
  }

  #hide = () => {
    if (!this.#toast || !this.#toast.classList.contains('is-visible')) return;
    this.#toast.classList.remove('is-visible');

    const onEnd = (/** @type {TransitionEvent} */ event) => {
      if (event.propertyName !== 'opacity') return;
      if (this.#toast && !this.#toast.classList.contains('is-visible')) this.#toast.hidden = true;
      this.#toast?.removeEventListener('transitionend', onEnd);
    };
    this.#toast.addEventListener('transitionend', onEnd);
  };

  #pause = () => {
    if (this.#progressBar) this.#progressBar.style.animationPlayState = 'paused';
  };

  #resume = () => {
    if (this.#progressBar) this.#progressBar.style.animationPlayState = 'running';
  };
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
